import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, forkJoin, Observable, zip } from 'rxjs';

import { parseDate } from '@progress/kendo-angular-intl';
import { NotificationService } from '@progress/kendo-angular-notification';
import {
  BaseEditService,
  SchedulerModelFields,
} from '@progress/kendo-angular-scheduler';
import _ from 'lodash';
import moment from 'moment';
import { DashboardPMResponse } from 'src/app/modules/provider-management/interfaces/pm-dashboard.response';
import { CancelEvaluatorModel, MeetingRequest } from 'src/app/modules/provider-management/models/schedule-evaluator-model';
import { ScheduleEventSaveModel } from 'src/app/modules/provider-management/models/schedule-save-model';
import { UpdateEventsModel } from 'src/app/modules/provider-management/models/update-events-model';
import {
  dialogAnimationDuration,
  dialogTimer,
} from 'src/app/shared/constants/dialog-specs';
import { User } from 'src/app/shared/models/user';
import { AuthStore } from 'src/app/stores/auth.store';
import {
  endOfWorkWeek,
  startOfWorkWeek,
} from '../../modules/administration/constants/scheduler';
import { IEPService } from '../../modules/iep/iep-document/services/iep.services';
import { ISchedulerResponse } from '../../modules/provider-management/interfaces/get-scheduler-response';
import { HolidayResponse, ProviderManagementService } from '../../modules/provider-management/services/provider-management.service';
import { ISedmsFormOutput } from '../interfaces/form/sedms-form-output.model';
import { LoadingService } from 'src/app/services/loading.service';

// Map Scheduler fields → your event model
const fields: SchedulerModelFields = {
  id: 'scheduleId', //or task id
  title: 'title',
  description: 'description',
  start: 'start',
  end: 'end',
  isAllDay: 'isAllDay',
  recurrenceRule: 'recurrenceRule',
  recurrenceId: 'recurrenceId',
  recurrenceExceptions: 'recurrenceException',
};

@Injectable({
  providedIn: 'root',
})
export class SchedulerEditService extends BaseEditService<MyEvent> {
  private _loading = false;

public get loading(): boolean {
  return this._loading;
}

public set loading(value: boolean) {
  this._loading = value;

  if (value) {
    this.loadingService.show();
  } else {
    this.loadingService.hide();
  }
}
  public selectedDate: Date = moment().toDate();
  private refershGrid: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);
  public refershGrid$: Observable<boolean> = this.refershGrid.asObservable();
  user: User;

  constructor(
    private http: HttpClient,
    private providerService: ProviderManagementService,
    public iepService: IEPService<ISedmsFormOutput>,
    private notificationService: NotificationService,
    private authStore: AuthStore,
    private loadingService: LoadingService
  ) {
    super(fields);
    this.authStore.UserInfo$.pipe(filter((user: User) => !!user)).subscribe(
      (userInfo: User) => {
        this.user = userInfo;
      }
    );
  }

  public updateRefreshSchedulerData(data: boolean): void {
    this.refershGrid.next(data);
  }
  /**
   * Read events from API
   */
  public read(
    providerSk: string,
    startDate: string,
    endDate: string,
    userTpe?: 'provider | evaluator' //Dev: Based on userType call diff APIs
  ): void {
    void userTpe;
    this.loading = true;

    forkJoin({
      schedules: this.providerService.getScheduleEvents(providerSk, startDate, endDate),
      holidays: this.providerService.getHolidays(startDate, endDate),
    }).subscribe(
      ({ schedules, holidays }) => {
        const parsedJsonArray: ISchedulerResponse[] = (schedules ?? []).map((item: ISchedulerResponse) => {
          const parsedJson: ISchedulerResponse = this.safeParse(item?.formJson);
          const parsedDeepFormJson: ISchedulerResponse = this.safeParse(
            _.get(parsedJson, 'formJson')
          );

          return {
            ...item,
            parsedDeepFormJson,
            parsedJson
          };
        });

        this.data = this.addGaps([
          ...this.buildHolidayEvents(holidays),
          ...parsedJsonArray.map((item: ISchedulerResponse) => item?.scheduleType === 'Evaluator' ? this.readEvalutorScheduleEvent(item) : this.readEvent(item)),
        ]);
        this.source.next(this.data);
        this.loading = false;
      },
      () => {
        this.loading = false;
      }
    );
  }

  public readEvaluatorScheduler(
    providerSk: string,
    startDate: string,
    endDate: string,
    userTpe?: 'provider | evaluator' //Dev: Based on userType call diff APIs
  ): void {
    void userTpe;
    this.loading = true;

    forkJoin({
      schedules: this.providerService.getMeetingEvaluatorSchedules(providerSk, startDate, endDate),
      holidays: this.providerService.getHolidays(startDate, endDate),
    }).subscribe(
      ({ schedules, holidays }) => {
        const parsedJsonArray: ISchedulerResponse[] = (schedules ?? []).map((item: ISchedulerResponse) => {
          const parsedJson: ISchedulerResponse = this.safeParse(item?.formJson);
          const parsedDeepFormJson = this.safeParse(
            _.get(parsedJson, 'formJson')
          );

          return {
            ...item,
            parsedDeepFormJson,
            parsedJson
          };
        });
        this.data = [
          ...this.buildHolidayEvents(holidays),
          ...parsedJsonArray.map((item: ISchedulerResponse) => this.readEvalutorScheduleEvent(item)),
        ];
        this.source.next(this.data);
        this.loading = false;
      },
      () => {
        this.loading = false;
      }
    );
  }

  public readTimeline(
    providerSk: string,
    startDate: string,
    endDate: string,
    userTpe?: 'provider | evaluator' //Dev: Based on userType call diff APIs
  ): void {
    void userTpe;
    // this.loading = true;
    // this.providerService
    //   .getMeetingSchedules(providerSk, startDate, endDate)
    //   .subscribe(
    //     (data: ISchedulerResponse[]) => {
    //       this.data = data.map((item: ISchedulerResponse) =>
    //         this.readEvent(item)
    //       );
    //       this.source.next(this.data);
    //       this.loading = false;
    //     },
    //     (err) => {
    // this.loading = false;
    //     }
    //   );
    // this.data = sampleData.map((item: any) => this.readEvent(item));
    // this.loading = false;
    // this.source.next(this.data);
  }

  /**
   * Schedule Provider Events
   */
  scheduleProviderEvents(eventPayload: ScheduleEventSaveModel, start?: any, end?: any): void {
    void start;
    void end;
    this.loading = true;
    this.providerService.submitPAMeeting(eventPayload).subscribe({
      next: () => {
        this.notificationService.show({
          content: 'Scheduled Events Successfully',
          hideAfter: dialogTimer,
          position: { horizontal: 'center', vertical: 'top' },
          animation: { type: 'slide', duration: dialogAnimationDuration },
          type: { style: 'success', icon: true },
        });
        this.loading = false;

        this.updateRefreshSchedulerData(true)
        // this.read(
        //   this.user?.providerSk,
        //   startOfWorkWeek,
        //   endOfWorkWeek
        // );
        // if (end) {
        //   const startDate: Date = new Date(start);
        //   const endDate: Date = new Date(end);
        //   this.read(this.user?.providerSk, formatStartToAPI(startDate), formatEndToAPI(endDate));
        // } else {
        //   const startDate: Date = new Date(start);
        //   const endDate: Date = new Date(startDate);
        //   this.read(this.user?.providerSk, formatStartToAPI(startDate), formatEndToAPIDate(endDate));
        // }
      },
      error: () => {
        this.notificationService.show({
          content: 'Scheduled Events Failed',
          hideAfter: dialogTimer,
          position: { horizontal: 'center', vertical: 'top' },
          animation: { type: 'slide', duration: dialogAnimationDuration },
          type: { style: 'error', icon: true },
        });
        this.loading = false;
      },
    });
  }


  scheduleEvaluationEvents(eventPayload: MeetingRequest, isAssigned?: boolean): void {
    void isAssigned;
    this.loading = true;
    this.providerService.submitEvaluationMeeting(eventPayload).subscribe({
      next: () => {
        this.notificationService.show({
          content: 'Scheduled Events Successfully',
          hideAfter: dialogTimer,
          position: { horizontal: 'center', vertical: 'top' },
          animation: { type: 'slide', duration: dialogAnimationDuration },
          type: { style: 'success', icon: true },
        });
        this.loading = false;
        //Fetch read
        // this.readEvaluatorScheduler(this.user?.providerSk, startOfWorkWeek, endOfWorkWeek);
        this.updateRefreshSchedulerData(true)
      },
      error: () => {
        this.notificationService.show({
          content: 'Scheduled Events Failed',
          hideAfter: dialogTimer,
          position: { horizontal: 'center', vertical: 'top' },
          animation: { type: 'slide', duration: dialogAnimationDuration },
          type: { style: 'error', icon: true },
        });
        this.loading = false;
      },
    });
  }

  /**
   * Update Schedule Provider Events
   */

  updateEvents(eventPayload: UpdateEventsModel): void {
    this.loading = true;
    this.providerService.updatePAMeeting(eventPayload).subscribe({
      next: () => {
        this.notificationService.show({
          content: 'Updated Events Successfully',
          hideAfter: dialogTimer,
          position: { horizontal: 'center', vertical: 'top' },
          animation: { type: 'slide', duration: dialogAnimationDuration },
          type: { style: 'success', icon: true },
        });
        this.loading = false;
        this.updateRefreshSchedulerData(true)
        //Fetch read
        // this.read(this.user?.providerSk, startOfWorkWeek, endOfWorkWeek);
      },
      error: () => {
        this.notificationService.show({
          content: 'Update Events Failed',
          hideAfter: dialogTimer,
          position: { horizontal: 'center', vertical: 'top' },
          animation: { type: 'slide', duration: dialogAnimationDuration },
          type: { style: 'error', icon: true },
        });
        this.loading = false;
      },
    });
  }


  cancelEvaluatorMeeting(eventPayload: CancelEvaluatorModel): void {
    this.loading = true;
    this.providerService.cancelEvaluatorMeeting(eventPayload).subscribe({
      next: () => {
        this.notificationService.show({
          content: 'Meeting cancelled  Successfully',
          hideAfter: dialogTimer,
          position: { horizontal: 'center', vertical: 'top' },
          animation: { type: 'slide', duration: dialogAnimationDuration },
          type: { style: 'success', icon: true },
        });
        this.loading = false;
        //Fetch read
        this.updateRefreshSchedulerData(true)
        // this.read(this.user?.providerSk, startOfWorkWeek, endOfWorkWeek);
      },
      error: () => {
        this.notificationService.show({
          content: 'Update Events Failed',
          hideAfter: dialogTimer,
          position: { horizontal: 'center', vertical: 'top' },
          animation: { type: 'slide', duration: dialogAnimationDuration },
          type: { style: 'error', icon: true },
        });
        this.loading = false;
      },
    });
  }
  updateEvaluatorScheduleEvents(eventPayload: MeetingRequest): void {
    this.loading = true;
    this.providerService.updateEvaluatorScheduleMeeting(eventPayload).subscribe({
      next: () => {
        this.notificationService.show({
          content: 'Updated Events Successfully',
          hideAfter: dialogTimer,
          position: { horizontal: 'center', vertical: 'top' },
          animation: { type: 'slide', duration: dialogAnimationDuration },
          type: { style: 'success', icon: true },
        });
        this.loading = false;
        //Fetch read
        this.read(this.user?.providerSk, startOfWorkWeek, endOfWorkWeek);
      },
      error: () => {
        this.notificationService.show({
          content: 'Update Events Failed',
          hideAfter: dialogTimer,
          position: { horizontal: 'center', vertical: 'top' },
          animation: { type: 'slide', duration: dialogAnimationDuration },
          type: { style: 'error', icon: true },
        });
        this.loading = false;
      },
    });
  }

  /**
   * Schedule Evaluator Events
   */
  //Dev: Uncomment below lines once evaluator implementation started
  // scheduleEvaluatorEvents(eventPayload): void{
  //call API
  // }
  /**
   * Save events (create, update, delete)
   */
  protected save(
    created: MyEvent[],
    updated: MyEvent[],
    deleted: MyEvent[]
  ): void {
    const completed: Observable<any>[] = [];

    if (deleted.length) {
      // completed.push(
      //   this.providerService.deleteMeetingSchedules(
      //     this.serializeModels(deleted)
      //   )
      // );
    }

    if (updated.length) {
      // completed.push(
      //   this.providerService.updateMeetingSchedules(
      //     this.serializeModels(updated)
      //   )
      // );
    }

    if (created.length) {
      // completed.push(
      //   this.providerService.createMeetingSchedules(
      //     this.serializeModels(created)
      //   )
      // );
    }

    if (completed.length) {
      zip(...completed).subscribe(() => {
        // reload with same params (you may need to pass them in)
        // this.read(providerSk, startDate, endDate);
      });
    }
  }

  buildHolidayEvents(holidays: HolidayResponse[] = []): MyEvent[] {
    return (holidays ?? []).map((holiday) => {
      const holidayDate = moment(holiday.holidayDate);
      const safeTitle = holiday.holidayName || 'Holiday';

      return {
        scheduleId: `holiday-${holidayDate.format('YYYYMMDD')}-${safeTitle.toLowerCase().replace(/\s+/g, '-')}`,
        title: safeTitle,
        description: safeTitle,
        start: holidayDate.startOf('day').toDate(),
        end: holidayDate.clone().add(1, 'day').startOf('day').toDate(),
        isAllDay: true,
        isHoliday: true,
        meetingDate: holidayDate.format('YYYY-MM-DD'),
        serviceType: safeTitle,
        location: safeTitle,
        providerName: 'Holiday',
      };
    });
  }

  private addGaps(events: MyEvent[]): MyEvent[] {
    const dwsByDay: Record<string, MyEvent[]> = _.groupBy(
      events.filter((e: MyEvent) => e.isDWSType && !e.isHoliday),
      (e: MyEvent) => moment(e.start).format('YYYY-MM-DD')
    );
    const gaps: MyEvent[] = Object.values(dwsByDay).flatMap((day: MyEvent[]) =>
      this.findGaps(_.sortBy(day, 'start'))
    );
    return [...events, ...gaps];
  }

  private findGaps(day: MyEvent[]): MyEvent[] {
    const gaps: MyEvent[] = [];
    let latestEnd: Date = day[0].end;
    day.slice(1).forEach((event: MyEvent) => {
      if (moment(event.start).isAfter(latestEnd)) {
        gaps.push({
          scheduleId: `gap-${latestEnd.getTime()}`,
          title: 'Gap',
          start: latestEnd,
          end: event.start,
          isGap: true,
        });
      }
      if (moment(event.end).isAfter(latestEnd)) {
        latestEnd = event.end;
      }
    });
    return gaps;
  }

  /**
   * Convert backend object → Scheduler event
   */
  private readEvent(item: any): MyEvent {
    return {
      scheduleType: item?.scheduleType,
      isDWSType: item?.isDWSType,
      scheduleId: item?.meetingSk,
      providerSk: item?.providersk,
      title: item?.serviceType,
      description: item?.location,
      start: parseDate(item?.meetingStartTime),
      end: parseDate(item?.meetingEndTime),
      isAllDay: false,
      language: item?.language,
      location: item?.location,
      frequency: item?.frequency,
      duration: item?.duration,
      serviceType: item?.serviceType,
      providerName: item?.providerName,
      providerId: item?.providerID,
      grade: item?.parsedJson?.studentMandates !== null ? item?.parsedJson?.studentMandates[0]?.grade : null,
      age: item?.parsedJson?.studentMandates !== null ? item?.parsedJson?.studentMandates[0]?.age : null,
      meetingDate: item?.meetingDate,
      assignmentSK: item?.assignmentSK,
      teamsMeeting: item?.parsedJson?.teamsMeeting,
      studentMandates: item?.parsedJson?.studentMandates,
      floorRoom: item?.parsedJson?.floorRoom,
      recurringMeetingSk: item?.recurringMeetingSk,
      studentName: item?.studentName,
      mandateDescription: item?.mandateDescription,
      sessionTypeName: item?.sessionTypeName,
      sessionType: item?.sessionType,
      locationType: item?.parsedJson?.locationType?.description,
      groupSize: item?.parsedJson?.groupSize,
      meetingSk: item?.meetingSk,
      startTime: item?.meetingStartTime,
      endTime: item?.meetingEndTime,
      encounterSK: item?.encounterSK,
      encounterStatus: item?.encounterStatus,
      sessionTypes: item?.parsedJson?.sessionType,
      locations: item?.location,
    };
  }

  private readEvalutorScheduleEvent(item: any): MyEvent {
    return {
      scheduleType: item?.scheduleType,
      scheduleId: item?.meetingSk,
      providerSk: item?.providersk,
      title: item?.evaluatorTypeName,
      description: item?.location,
      nycid: item?.parsedDeepFormJson?.nycId,
      start: parseDate(item?.meetingStartTime),
      end: parseDate(item?.meetingEndTime),
      isAllDay: false,
      language: item?.parsedDeepFormJson?.language,
      location: item?.parsedDeepFormJson?.location,
      frequency: item?.frequency,
      duration: item?.duration,
      serviceType: item?.evaluatorTypeName,
      providerName: item?.providerName,
      providerId: item?.providerID,
      grade: item?.parsedDeepFormJson?.grade,
      age: item?.parsedDeepFormJson?.age,
      meetingDate: item?.meetingDate,
      assignmentSK: item?.assignmentSK,
      teamsMeeting: item?.parsedDeepFormJson?.teamsMeeting,
      studentMandates: item?.parsedDeepFormJson?.students,
      floorRoom: item?.parsedDeepFormJson?.floorRoom,
      recurringMeetingSk: item?.recurringMeetingSk,
      studentName: item?.parsedDeepFormJson?.studentName,
      mandateDescription: item?.mandateDescription,
      sessionTypeName: item?.sessionTypeName,
      sessionType: item?.sessionType,
      locationType: item?.parsedDeepFormJson?.locationType?.description,
      referralSK: item?.referralSK,
      evaluatorTypeName: item?.evaluatorTypeName,
      evaluationSk: item?.evaluationSk,
      studentSk: item?.studentSk,
      encounterSK: item?.encounterSK,
      encounterStatus: item?.encounterStatus,
      students: item?.parsedDeepFormJson?.students,
      sessionTypes: item?.parsedJson?.sessionType,

    };
  }
  /**
   * Convert Scheduler event → backend object
   */
  private serializeModels(events: MyEvent[]): any[] {
    if (!events) return [];

    return events.map((event: MyEvent) => ({
      meetingSk: event.scheduleId ?? '00000000-0000-0000-0000-000000000000',
      providersk: event.providerSk,
      location: event.description,
      meetingDate: this.toApiDateTime(event.start),
      meetingStartTime: this.toApiDateTime(event.start),
      meetingEndTime: this.toApiDateTime(event.end),
      language: 'EN', // placeholder
      serviceType: event.title,
      duration: this.calculateDuration(event.start, event.end),
      frequency: '2', // placeholder
      groupSize: '',
    }));
  }

  private toApiDateTime(date: Date | undefined): string | null {
    return date ? moment(date).format('YYYY-MM-DDTHH:mm:ss') : null;
  }

  private calculateDuration(start?: Date, end?: Date): string {
    if (!start || !end) return '0';
    const minutes: number = moment(end).diff(moment(start), 'minutes');
    return (minutes / 60).toString();
  }

  private safeParse<T = any>(value: any): T | null {
    return _.isString(value) && value.trim().startsWith('{')
      ? _.attempt(() => JSON.parse(value)) || null
      : null;
  }
}

/**
 * Scheduler event model
 */
export interface MyEvent {
  scheduleId?: string; // maps to meetingSk
  providerSk?: string;
  title?: string; // serviceType
  description?: string; // location
  start?: Date;
  end?: Date;
  nycid?: string;
  isAllDay?: boolean;
  recurrenceRule?: string;
  recurrenceId?: string;
  recurrenceException?: any;
  language?: string;
  location?: string;
  frequency?: string;
  duration?: string;
  serviceType?: string;
  providerName?: string;
  providerId?: string;
  grade?: string;
  age?: number;
  meetingDate?: string;
  assignmentSK?: string;
  teamsMeeting?: boolean;
  floorRoom?: string;
  studentMandates?: DashboardPMResponse[];
  recurringMeetingSk?: string;
  studentName?: string;
  mandateDescription?: string;
  sessionTypeName?: string;
  sessionType?: number;
  locationType?: string;
  groupSize?: string;
  meetingSk?: string;
  startTime?: string;
  endTime?: string;
  referralSK?: string;
  evaluatorTypeName?: string;
  evaluationSk?: string;
  studentSk?: string;
  encounterSK?: string;
  encounterStatus?: string;
  students?: DashboardPMResponse;
  sessionTypes?: string;
  locations?: string;
  isHoliday?: boolean;
  scheduleType?: 'Provider' | 'Evaluator';
  isDWSType?: boolean;
  isGap?: boolean;
}
